/**
 * Losses Page
 *
 * Main page for losses management with quick entry, KPIs, and data grid.
 * @see Sprint 13 - Pertes
 * @see US-13.6 - UI Page Pertes
 * @see US-13.8 - Deep Linking
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { fetchAllForExport } from '@bill/ui';
import { Skeleton, EmptyState } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import { formatDateParam } from '@/components/ui';
import { useDrawerRoute, useDebounce, usePermission, useCanSeeFinancials } from '@/hooks';
import {
  useLosses,
  useCancelLoss,
} from '../hooks/useLosses';
import { lossesService } from '../services/losses.service';
import {
  LossQuickForm,
  LossesKPIs,
  LossesFilters,
  LossesTable,
  LossDetailDrawer,
} from '../components';
import type { LossListItem, QueryLossesDto, LossReason, LossType } from '@bstock/shared';

type SortField = QueryLossesDto['sortBy'];
type SortOrder = QueryLossesDto['sortOrder'];
const DEFAULT_LIMIT = 20;

function parseUrlParams(searchParams: URLSearchParams) {
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const defaultStart = new Date();
  defaultStart.setDate(1); // 1er du mois
  return {
    dateRange: {
      start: startDate ? new Date(startDate) : defaultStart,
      end: endDate ? new Date(endDate) : new Date(),
    } as DateRange,
    lossReason: searchParams.get('lossReason') ?? '',
    lossType: searchParams.get('lossType') ?? '',
    search: searchParams.get('search') ?? '',
    page: parseInt(searchParams.get('page') ?? '1', 10),
    sortBy: (searchParams.get('sortBy') as SortField) ?? 'eventDate',
    sortOrder: (searchParams.get('sortOrder') as SortOrder) ?? 'desc',
  };
}

export default function LossesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Permissions
  const canCreate = usePermission('losses.create');
  const canDelete = usePermission('losses.delete');
  const showFinancials = useCanSeeFinancials();

  // Parse URL params for deep linking
  const urlParams = useMemo(() => parseUrlParams(searchParams), [searchParams]);

  // Drawer routing with deep linking
  const drawer = useDrawerRoute('/stock/pertes', { paramName: 'lossId' });

  // Filters state from URL
  const [dateRange, setDateRange] = useState<DateRange>(urlParams.dateRange);
  const [lossReasonFilter, setLossReasonFilter] = useState(urlParams.lossReason);
  const [lossTypeFilter, setLossTypeFilter] = useState(urlParams.lossType);
  const [searchQuery, setSearchQuery] = useState(urlParams.search);
  const [page, setPage] = useState(urlParams.page);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [sortBy, setSortBy] = useState<SortField>(urlParams.sortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(urlParams.sortOrder);

  // Debounce search
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Update URL when filters change
  const updateUrl = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Query params
  const queryParams = useMemo((): QueryLossesDto => ({
    page,
    limit,
    startDate: formatDateParam(dateRange.start) ?? undefined,
    endDate: formatDateParam(dateRange.end) ?? undefined,
    lossReason: lossReasonFilter ? (lossReasonFilter as LossReason) : undefined,
    lossType: lossTypeFilter ? (lossTypeFilter as LossType) : undefined,
    search: debouncedSearch || undefined,
    sortBy,
    sortOrder,
  }), [page, limit, dateRange, lossReasonFilter, lossTypeFilter, debouncedSearch, sortBy, sortOrder]);

  // Fetch losses
  const { data: lossesData, isLoading } = useLosses(queryParams);

  // Cancel mutation
  const cancelMutation = useCancelLoss();

  const losses = lossesData?.data ?? [];
  const pagination = lossesData?.pagination;

  // Handlers
  const handleRowClick = useCallback(
    (loss: LossListItem) => {
      drawer.open(loss.id);
    },
    [drawer]
  );

  const handleCancel = useCallback(
    async (loss: LossListItem) => {
      await cancelMutation.mutateAsync(loss.id);
    },
    [cancelMutation]
  );

  const handleSort = useCallback((field: SortField, order: SortOrder) => {
    setSortBy(field);
    setSortOrder(order);
    setPage(1);
    updateUrl({ sortBy: field || null, sortOrder: order || null, page: null });
  }, [updateUrl]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    updateUrl({ page: String(newPage) });
  }, [updateUrl]);

  const handleLimitChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
    updateUrl({ page: null });
  }, [updateUrl]);

  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setPage(1);
    updateUrl({
      startDate: formatDateParam(range.start),
      endDate: formatDateParam(range.end),
      page: null,
    });
  }, [updateUrl]);

  const handleLossReasonChange = useCallback((newReason: string) => {
    setLossReasonFilter(newReason);
    setPage(1);
    updateUrl({ lossReason: newReason || null, page: null });
  }, [updateUrl]);

  const handleLossTypeChange = useCallback((newType: string) => {
    setLossTypeFilter(newType);
    setPage(1);
    updateUrl({ lossType: newType || null, page: null });
  }, [updateUrl]);

  const handleSearchChange = useCallback((newSearch: string) => {
    setSearchQuery(newSearch);
    setPage(1);
    updateUrl({ search: newSearch || null, page: null });
  }, [updateUrl]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-danger-100 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-danger-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-dark-500">Pertes</h1>
            <p className="text-sm text-neutral-500 mt-0.5">
              Enregistrez et suivez les pertes de stock
            </p>
          </div>
        </div>
      </div>

      {/* Quick Entry Form - Sticky */}
      {canCreate && <LossQuickForm />}

      {/* KPIs */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <LossesKPIs period="30days" showFinancials={showFinancials} />
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-neutral-200 px-6 py-3 flex-shrink-0">
        <LossesFilters
          dateRange={dateRange}
          lossReason={lossReasonFilter}
          lossType={lossTypeFilter}
          search={searchQuery}
          onDateRangeChange={handleDateRangeChange}
          onLossReasonChange={handleLossReasonChange}
          onLossTypeChange={handleLossTypeChange}
          onSearchChange={handleSearchChange}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-neutral-50">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : losses.length === 0 ? (
          <EmptyState
            icon={AlertTriangle}
            title="Aucune perte enregistree"
            description="Utilisez le formulaire de saisie rapide ci-dessus pour enregistrer une perte"
          />
        ) : (
          <LossesTable
            losses={losses}
            pagination={pagination ?? { page: 1, limit, total: 0, totalPages: 0 }}
            isLoading={isLoading}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onRowClick={handleRowClick}
            onCancel={canDelete ? handleCancel : undefined}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            onFetchAllRows={() =>
              fetchAllForExport(async ({ limit: exportLimit }) => {
                const response = await lossesService.list({
                  ...queryParams,
                  page: 1,
                  limit: exportLimit,
                });
                return {
                  items: response.data,
                  pagination: { total: response.pagination.total },
                };
              })
            }
            showFinancials={showFinancials}
            dateFrom={formatDateParam(dateRange.start) ?? undefined}
            dateTo={formatDateParam(dateRange.end) ?? undefined}
          />
        )}
      </div>

      {/* Loss Detail Drawer */}
      <LossDetailDrawer
        lossId={drawer.resourceId}
        open={drawer.isOpen}
        onOpenChange={(open) => !open && drawer.close()}
        showFinancials={showFinancials}
        canCancel={canDelete}
      />
    </div>
  );
}
